/**
 * Taint editor dialog (#256).
 *
 * Admin-tier editor for a node's taints. Loads the current taints into a
 * row-per-taint UI, lets the operator add/remove/edit rows, and PATCHes
 * the entire array on save (server-side semantics: strategic-merge patch
 * replaces the array, since K8s taints have no merge key).
 *
 * NoExecute warning: any row with effect=NoExecute reveals an inline
 * banner ("NoExecute will evict existing pods that don't tolerate this
 * taint."). The warning is intentionally per-dialog and not per-row to
 * keep the UI dense — admins editing taints know what they're doing, but
 * a single visible reminder catches the most common foot-gun.
 *
 * The node-pool taint (JSS#1104) is shown read-only above the rows and left
 * out of the save: only the site's node pools set it, and the server keeps
 * the one on the node.
 */
import { useState } from 'react';
import { X, AlertTriangle, Trash2, Plus, Lock } from 'lucide-react';
import type { ClusterNode, NodeTaint, TaintEffect } from '@/types';
import { isNodePoolTaintKey, NODE_POOL_TAINT_KEY } from '@/lib/nodePools';

interface Props {
  node: ClusterNode;
  onSave: (taints: NodeTaint[]) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const TAINT_EFFECTS: TaintEffect[] = ['NoSchedule', 'PreferNoSchedule', 'NoExecute'];

function isValidTaint(t: { key: string; effect: string }): t is NodeTaint {
  return !!t.key && (TAINT_EFFECTS as string[]).includes(t.effect) && !isNodePoolTaintKey(t.key);
}

export default function TaintEditorDialog({ node, onSave, onCancel, isLoading }: Props) {
  const poolTaints = (node.taints ?? []).filter((t) => isNodePoolTaintKey(t.key));
  const [rows, setRows] = useState<{ key: string; value: string; effect: TaintEffect }[]>(() =>
    (node.taints ?? []).filter((t) => !isNodePoolTaintKey(t.key)).map((t) => ({
      key: t.key,
      value: t.value ?? '',
      // Existing taints can carry effect strings outside the canonical
      // three; coerce unknowns to NoSchedule rather than crash the editor.
      effect: (TAINT_EFFECTS as string[]).includes(t.effect)
        ? (t.effect as TaintEffect)
        : 'NoSchedule',
    })),
  );

  const hasNoExecute = rows.some((r) => r.effect === 'NoExecute');
  const allValid = rows.every((r) => isValidTaint(r));
  const usesPoolKey = rows.some((r) => isNodePoolTaintKey(r.key));

  function updateRow(idx: number, patch: Partial<typeof rows[number]>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function addRow() {
    setRows((prev) => [...prev, { key: '', value: '', effect: 'NoSchedule' }]);
  }

  function removeRow(idx: number) {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleSave() {
    const taints: NodeTaint[] = rows
      .filter(isValidTaint)
      .map((r) => ({
        key: r.key,
        value: r.value || undefined,
        effect: r.effect,
      }));
    onSave(taints);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-surface border border-surface-elevated rounded-xl w-full max-w-2xl mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <h3 className="text-silver-bright font-semibold">Edit Taints — {node.name}</h3>
          <button onClick={onCancel} className="text-silver-muted hover:text-silver-bright transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-3">
          {hasNoExecute && (
            <div
              role="alert"
              data-testid="noexecute-warning"
              className="flex items-start gap-2 rounded-lg border border-status-error/40 bg-status-error/10 px-3 py-2 text-xs text-status-error"
            >
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>
                <strong className="font-semibold">NoExecute</strong> will evict existing pods that don&apos;t
                tolerate this taint.
              </span>
            </div>
          )}

          {poolTaints.map((t) => (
            <div
              key={`${t.key}=${t.value ?? ''}:${t.effect}`}
              data-testid="pool-taint-row"
              className="flex items-center gap-2 rounded-lg border border-surface-elevated bg-surface-deep/60 px-3 py-2 text-xs text-silver-muted"
            >
              <Lock size={12} className="shrink-0" />
              <span className="font-mono text-silver-bright">
                {t.key}={t.value ?? ''}:{t.effect}
              </span>
              <span>Set by the site&apos;s node pools — change it under Node pools.</span>
            </div>
          ))}

          {usesPoolKey && (
            <div role="alert" data-testid="pool-key-error" className="text-xs text-status-error">
              <span className="font-mono">{NODE_POOL_TAINT_KEY}</span> is reserved for node pools.
            </div>
          )}

          {rows.length === 0 ? (
            <div className="text-sm text-silver-muted py-2">No taints. Add one below.</div>
          ) : (
            <div className="space-y-2">
              {rows.map((row, idx) => (
                <div
                  key={idx}
                  data-testid="taint-row"
                  className="grid grid-cols-[2fr_2fr_1.4fr_auto] gap-2 items-center"
                >
                  <input
                    type="text"
                    aria-label="taint key"
                    placeholder="key"
                    value={row.key}
                    onChange={(e) => updateRow(idx, { key: e.target.value })}
                    className="bg-surface-deep border border-surface-elevated rounded px-2 py-1.5 text-sm text-silver-bright font-mono focus:border-gold focus:outline-none"
                  />
                  <input
                    type="text"
                    aria-label="taint value"
                    placeholder="value (optional)"
                    value={row.value}
                    onChange={(e) => updateRow(idx, { value: e.target.value })}
                    className="bg-surface-deep border border-surface-elevated rounded px-2 py-1.5 text-sm text-silver-bright font-mono focus:border-gold focus:outline-none"
                  />
                  <select
                    aria-label="taint effect"
                    value={row.effect}
                    onChange={(e) => updateRow(idx, { effect: e.target.value as TaintEffect })}
                    className="bg-surface-deep border border-surface-elevated rounded px-2 py-1.5 text-sm text-silver-bright focus:border-gold focus:outline-none"
                  >
                    {TAINT_EFFECTS.map((eff) => (
                      <option key={eff} value={eff}>{eff}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => removeRow(idx)}
                    aria-label="remove taint"
                    className="p-1.5 text-silver-muted hover:text-status-error transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={addRow}
            className="flex items-center gap-1 text-xs text-gold hover:text-gold-light transition-colors"
          >
            <Plus size={12} /> Add taint
          </button>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!allValid || isLoading}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-gold text-surface-deep hover:bg-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
